import React, { useState, useEffect } from 'react';
import pb from '../lib/pocketbase';
import { Pencil, Trash2, Plus, X, GripVertical, ArrowUp, ArrowDown } from 'lucide-react';
import './AdminCrud.css';

const AdminNews = () => {
    const [news, setNews] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [draggedItem, setDraggedItem] = useState(null);
    const [dragOverItem, setDragOverItem] = useState(null);

    // Form state
    const [formData, setFormData] = useState({
        titre: '',
        contenu: '',
        date: new Date().toISOString().split('T')[0],
        actif: true,
        image: null
    });

    useEffect(() => {
        fetchNews();
    }, []);

    const fetchNews = async () => {
        setLoading(true);
        try {
            const records = await pb.collection('actualites').getList(1, 100, {
                sort: '+ordre', // Manual sorting
            });
            setNews(records.items);
        } catch (err) {
            console.error("Error fetching news:", err);
            // Fallback for first time or missing field
            try {
                const records = await pb.collection('actualites').getList(1, 100, {
                    sort: '-date',
                });
                setNews(records.items);
            } catch (retry) { }
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked, files } = e.target;
        if (type === 'file') {
            setFormData({ ...formData, [name]: files[0] });
        } else if (type === 'checkbox') {
            setFormData({ ...formData, [name]: checked });
        } else {
            setFormData({ ...formData, [name]: value });
        }
    };

    const openModal = (item = null) => {
        if (item) {
            setEditingItem(item);
            setFormData({
                titre: item.titre,
                contenu: item.contenu,
                date: item.date ? item.date.split('T')[0] : new Date().toISOString().split('T')[0],
                actif: item.actif,
                image: null
            });
        } else {
            setEditingItem(null);
            setFormData({
                titre: '',
                contenu: '',
                date: new Date().toISOString().split('T')[0],
                actif: true,
                image: null
            });
        }
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setEditingItem(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            const data = new FormData();
            data.append('titre', formData.titre);
            data.append('contenu', formData.contenu);
            const dateObj = new Date(formData.date);
            data.append('date', dateObj.toISOString());
            data.append('actif', formData.actif);

            if (formData.image) {
                data.append('image', formData.image);
            }

            if (editingItem) {
                await pb.collection('actualites').update(editingItem.id, data);
            } else {
                await pb.collection('actualites').create(data);
            }

            closeModal();
            fetchNews();
        } catch (err) {
            console.error("Error saving news:", err);
            alert("Erreur lors de la sauvegarde.");
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm("Êtes-vous sûr de vouloir supprimer cette actualité ?")) {
            try {
                await pb.collection('actualites').delete(id);
                fetchNews();
            } catch (err) {
                console.error("Error deleting:", err);
            }
        }
    };

    const handleMove = async (index, direction) => {
        if (direction === 'up' && index === 0) return;
        if (direction === 'down' && index === news.length - 1) return;

        const otherIndex = direction === 'up' ? index - 1 : index + 1;
        const currentItem = news[index];
        const otherItem = news[otherIndex];

        const currentOrder = currentItem.ordre || index + 1;
        const otherOrder = otherItem.ordre || otherIndex + 1;

        try {
            await Promise.all([
                pb.collection('actualites').update(currentItem.id, { ordre: otherOrder }),
                pb.collection('actualites').update(otherItem.id, { ordre: currentOrder })
            ]);
            await fetchNews();
        } catch (err) {
            console.error("Error reordering:", err);
        }
    };

    // Drag and Drop Handlers
    const onDragStart = (e, index) => {
        setDraggedItem(news[index]);
        e.dataTransfer.effectAllowed = "move";
        e.target.style.opacity = '0.5';
    };

    const onDragOver = (e, index) => {
        e.preventDefault();
        setDragOverItem(index);
    };

    const onDragEnd = (e) => {
        e.target.style.opacity = '1';
        setDragOverItem(null);
    };

    const onDrop = async (e, targetIndex) => {
        e.preventDefault();
        const sourceIndex = news.findIndex(p => p.id === draggedItem.id);
        if (sourceIndex === targetIndex) return;

        const newNews = [...news];
        const [removed] = newNews.splice(sourceIndex, 1);
        newNews.splice(targetIndex, 0, removed);

        setNews(newNews);

        try {
            const updates = newNews.map((p, idx) =>
                pb.collection('actualites').update(p.id, { ordre: idx + 1 })
            );
            await Promise.all(updates);
            fetchNews();
        } catch (err) {
            console.error("Error updating order after drop:", err);
            fetchNews();
        }
    };

    if (loading) return <div>Chargement...</div>;

    return (
        <div className="admin-crud-container">
            <div className="admin-tools">
                <button className="btn" onClick={() => openModal()}>
                    <Plus size={16} style={{ marginRight: '5px' }} /> Ajouter une actualité
                </button>
            </div>

            <div className="crud-table-wrapper">
                <table className="crud-table" style={{ minWidth: '600px' }}>
                    <thead>
                        <tr>
                            <th style={{ width: '40px' }}></th>
                            <th>Date</th>
                            <th>Image</th>
                            <th>Titre</th>
                            <th>Visible</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {news.map((item, index) => (
                            <tr
                                key={item.id}
                                draggable
                                onDragStart={(e) => onDragStart(e, index)}
                                onDragOver={(e) => onDragOver(e, index)}
                                onDragEnd={onDragEnd}
                                onDrop={(e) => onDrop(e, index)}
                                className={dragOverItem === index ? 'drag-over' : ''}
                            >
                                <td className="drag-handle-cell">
                                    <GripVertical size={18} className="drag-handle-icon" />
                                </td>
                                <td>{new Date(item.date).toLocaleDateString()}</td>
                                <td>
                                    {item.image ? (
                                        <img
                                            src={pb.files.getURL(item, item.image, { thumb: '100x100' })}
                                            alt={item.titre}
                                            className="thumbnail"
                                        />
                                    ) : (
                                        <span style={{ color: '#ccc' }}>Sans img</span>
                                    )}
                                </td>
                                <td>{item.titre}</td>
                                <td>{item.actif ? 'Oui' : 'Non'}</td>
                                <td>
                                    <div style={{ display: 'flex', gap: '5px' }}>
                                        <button className="action-btn btn-move" onClick={() => handleMove(index, 'up')} disabled={index === 0}>
                                            <ArrowUp size={14} />
                                        </button>
                                        <button className="action-btn btn-move" onClick={() => handleMove(index, 'down')} disabled={index === news.length - 1}>
                                            <ArrowDown size={14} />
                                        </button>
                                        <button className="action-btn btn-edit" onClick={() => openModal(item)}>
                                            <Pencil size={14} />
                                        </button>
                                        <button className="action-btn btn-delete" onClick={() => handleDelete(item.id)}>
                                            <Trash2 size={14} />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                        {news.length === 0 && (
                            <tr>
                                <td colSpan="5" style={{ textAlign: 'center', padding: '20px' }}>Aucune actualité.</td>
                            </tr>
                        )}
                    </tbody>
                </table>

                {/* Modal */}
                {isModalOpen && (
                    <div className="modal-overlay" onClick={closeModal}>
                        <div className="modal-content" onClick={e => e.stopPropagation()}>
                            <div className="modal-header">
                                <h2>{editingItem ? 'Modifier' : 'Ajouter'} une actualité</h2>
                                <button className="close-btn" onClick={closeModal} style={{ border: 'none', background: 'none' }}><X /></button>
                            </div>
                            <form onSubmit={handleSubmit}>
                                <div className="form-group">
                                    <label>Titre</label>
                                    <input
                                        type="text"
                                        name="titre"
                                        value={formData.titre}
                                        onChange={handleInputChange}
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Date</label>
                                    <input
                                        type="date"
                                        name="date"
                                        value={formData.date}
                                        onChange={handleInputChange}
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Contenu</label>
                                    <textarea
                                        name="contenu"
                                        value={formData.contenu}
                                        onChange={handleInputChange}
                                        rows="5"
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label>
                                        <input
                                            type="checkbox"
                                            name="actif"
                                            checked={formData.actif}
                                            onChange={handleInputChange}
                                            style={{ width: 'auto', marginRight: '10px' }}
                                        />
                                        Visible sur le site
                                    </label>
                                </div>
                                <div className="form-group">
                                    <label>Image</label>
                                    <input
                                        type="file"
                                        name="image"
                                        onChange={handleInputChange}
                                        accept="image/*"
                                    />
                                </div>
                                <div className="modal-actions">
                                    <button type="button" className="btn" style={{ background: '#999' }} onClick={closeModal}>Annuler</button>
                                    <button type="submit" className="btn">Sauvegarder</button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminNews;
